import { useRuntimeConfig } from "#imports";
import { useConsentStore } from "~~/stores/consentStore";

/**
 * edrone — behawioralny tracking odtworzony dla frontu headless (Nuxt).
 *
 * W klasyku moduł `edrone` wstrzykiwał tracking przez hooki Smarty
 * (displayHeader / displayOrderConfirmation / displayNewsletterRegistration),
 * których Nuxt nie renderuje — więc po cutoverze na headless edrone przestał
 * zbierać zdarzenia (product/category view, add_to_cart, order, subscribe).
 * Ten composable jest odpowiednikiem `modules/edrone/views/js/edrone.ts`:
 * ładuje SDK edrone z CDN, buduje `window._edrone` i wysyła zdarzenia przez
 * `_edrone.init()` — bez zależności od `window.prestashop`, którego na froncie
 * nie ma. Zdarzenia pushowane są z centralnego huba `useAnalytics`.
 *
 * Gate zgody: marketing (jak Meta Pixel). Bez zgody SDK się nie ładuje.
 *
 * Fallback przez backend (obejście adblocków): jeśli SDK z CDN jest
 * zablokowany, a env NUXT_PUBLIC_EDRONE_BACKEND_TRACE=true, zdarzenie leci
 * POST-em do proxy `/api/store/edrone/trace` → istniejący kontroler
 * `EdroneTraceModuleFrontController` na backendzie, który forwarduje je do
 * api.edrone.me (z cookies fp_sid/fp_ccid). To odpowiednik oryginalnego
 * `sendTraceThroughBackend` sterowanego switchem EDRONE_SEND_TRACES_THROUGH_BACKEND.
 */

// Odpowiada EDRONE_VERSION / _PS_VERSION_ z modułu — SDK edrone raportuje je jako
// wersję integracji i platformy. Stałe, bo nie zmieniają się per-deploy frontu.
const EDRONE_VERSION = "2.3.0";
const EDRONE_PLATFORM_VERSION = "9.0.3";
const EDRONE_PLATFORM = "prestashop" as const;
const EDRONE_SDK_SRC = "//d3bo67muzbfgtl.cloudfront.net/edrone_2_0.js";

/** Pola czyszczone przed KAŻDYM zdarzeniem, by wartości z poprzedniego trace nie
 *  wyciekały do następnego (1:1 z `clearableEdroneKeys` z edrone.ts). */
const CLEARABLE_KEYS = [
  "product_skus", "product_ids", "product_counts",
  "product_category_ids", "product_category_names",
  "order_id", "city", "country", "state",
  "order_currency", "base_currency",
  "base_payment_value", "order_payment_value",
  "event_utc_date", "company", "customer_tags", "birth_date",
  "email", "phone", "first_name", "last_name", "user_id",
] as const;

interface EdroneWindow {
  app_id: string;
  version: string;
  platform_version: string;
  platform: string;
  init?: () => void;
  [key: string]: unknown;
}

declare global {
  interface Window { _edrone?: EdroneWindow; }
}

// Stan współdzielony między wywołaniami composable (jak `pixelBootstrapped`
// w useMetaTracking). SDK jest jeden na dokument.
let sdkAttached = false;
const pending: Record<string, unknown>[] = [];
let draining = false;
let drainAttempts = 0;
// SDK z CDN ładuje się async; ~8 s (20 × 400 ms) na pojawienie się _edrone.init,
// zanim uznamy że jest zablokowany (adblock) i przejdziemy na fallback backendu.
const MAX_DRAIN_ATTEMPTS = 20;
const DRAIN_INTERVAL_MS = 400;

export function useEdroneTracking() {
  const config = useRuntimeConfig();
  const appId = String((config.public as Record<string, unknown>).edroneAppId || "");
  const backendTrace = Boolean((config.public as Record<string, unknown>).edroneBackendTrace);
  const consent = useConsentStore();

  function marketingAllowed(): boolean {
    return !!consent.current?.marketing;
  }

  function attachSdk(): void {
    if (sdkAttached || typeof document === "undefined") return;
    window._edrone = {
      app_id: appId,
      version: EDRONE_VERSION,
      platform_version: EDRONE_PLATFORM_VERSION,
      platform: EDRONE_PLATFORM,
    };
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = `${EDRONE_SDK_SRC}?app_id=${encodeURIComponent(appId)}`;
    document.head.appendChild(script);
    sdkAttached = true;
  }

  /** Wysyła jedno zdarzenie przez SDK: czyści dynamiczne pola, wstawia nowe i
   *  woła `_edrone.init()` (identycznie jak `sendEvent` w edrone.ts). */
  function sendViaSdk(event: Record<string, unknown>): boolean {
    const ed = window._edrone;
    if (!ed || typeof ed.init !== "function") return false;
    try {
      for (const key of CLEARABLE_KEYS) ed[key] = undefined;
      Object.assign(ed, event);
      ed.init();
      return true;
    } catch {
      return false;
    }
  }

  /** Fallback: proxy backendu (obejście adblocków). Fire-and-forget. */
  function sendViaBackend(event: Record<string, unknown>): void {
    if (!backendTrace) return;
    try {
      $fetch("/api/store/edrone/trace", {
        method: "POST",
        credentials: "include",
        body: {
          ...event,
          app_id: appId,
          version: EDRONE_VERSION,
          platform_version: EDRONE_PLATFORM_VERSION,
          platform: EDRONE_PLATFORM,
        },
      }).catch(() => { /* tracking nie blokuje UX */ });
    } catch { /* noop */ }
  }

  /** Opróżnia kolejkę przez SDK; jeśli SDK jeszcze się ładuje — ponawia; jeśli
   *  po MAX próbach nadal go nie ma (zablokowany) — przełącza na backend. */
  function drain(): void {
    if (!pending.length) { draining = false; return; }

    if (window._edrone && typeof window._edrone.init === "function") {
      drainAttempts = 0;
      while (pending.length) sendViaSdk(pending.shift()!);
      draining = false;
      return;
    }

    if (drainAttempts++ < MAX_DRAIN_ATTEMPTS) {
      draining = true;
      setTimeout(drain, DRAIN_INTERVAL_MS);
      return;
    }

    // SDK się nie pojawił — traktujemy jako zablokowany i lecimy backendem.
    while (pending.length) sendViaBackend(pending.shift()!);
    draining = false;
  }

  function pushEvent(event: Record<string, unknown>): void {
    if (typeof window === "undefined") return;
    if (!appId || !marketingAllowed()) return;
    attachSdk();
    pending.push(event);
    if (!draining) drain();
  }

  /**
   * Ładuje SDK edrone OD RAZU, gdy jest zgoda marketingowa — bez czekania na
   * pierwsze zdarzenie trackingu. Bez tego SDK wstrzykuje się dopiero wewnątrz
   * `pushEvent`, a te padają zwykle dopiero przy nawigacji (product/category
   * view). Efekt: po kliknięciu „Akceptuję" na bieżącej stronie skrypt edrone
   * nie startował aż do przeładowania/przejścia. Wołane z pluginu consent.client
   * przy udzieleniu zgody i przy starcie dla powracającego usera. Idempotentne
   * (attachSdk ma własny guard `sdkAttached`).
   */
  function bootstrap(): void {
    if (typeof window === "undefined") return;
    if (!appId || !marketingAllowed()) return;
    attachSdk();
  }

  function trackProductView(productId: string | number): void {
    pushEvent({ action_type: "product_view", product_ids: String(productId) });
  }

  function trackCategoryView(categoryId: string | number, categoryName?: string): void {
    pushEvent({
      action_type: "category_view",
      product_category_ids: String(categoryId),
      ...(categoryName ? { product_category_names: categoryName } : {}),
    });
  }

  function trackAddToCart(productId: string | number): void {
    pushEvent({ action_type: "add_to_cart", product_ids: String(productId) });
  }

  function trackOrder(order: Record<string, unknown>): void {
    pushEvent({ action_type: "order", customer_tags: "Order", ...order });
  }

  function trackSubscribe(subscribe: {
    email: string;
    customer_tags: string;
    first_name?: string;
    last_name?: string;
    user_id?: string;
  }): void {
    if (!subscribe.email) return;
    pushEvent({ action_type: "subscribe", ...subscribe });
  }

  return { bootstrap, pushEvent, trackProductView, trackCategoryView, trackAddToCart, trackOrder, trackSubscribe };
}
